import {clsx} from 'clsx';
import {Slot as RadixSlot} from 'radix-ui';
import {
  forwardRef,
  isValidElement,
  memo,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  PropsWithChildren,
  ReactElement,
} from 'react';

import type {SizeUpTo} from '#theme';
import {
  BaseColor,
  Color,
  colorVarMap,
  getBaseColor,
  isVariantColor,
  Size,
} from '#theme';
import {is} from '#utils/predicates.js';
import {mergeRefs} from '#utils/react.js';

import type {Icons} from '../icon/icons.js';
import {Icon} from '../icon/index.js';
import {Spinner} from '../spinner/index.js';
import * as styles from './style.module.scss';

export const ButtonVariant = {
  Base: 'base',
  Borderless: 'borderless',
  Danger: 'danger',
  Link: 'link',
  Outlined: 'outlined',
};

export type ButtonVariant = (typeof ButtonVariant)[keyof typeof ButtonVariant];

export type ButtonProps = PropsWithChildren<
  {
    disabled?: boolean;
    variant?: ButtonVariant;
    size?: SizeUpTo<4>;
    color?: Color;
    loading?: boolean;
    loadingInline?: boolean;
    icon?: Icons | ReactElement;
    type?: 'button' | 'submit' | 'reset';
    asChild?: boolean;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>;

const buttonIconSizeMap: Partial<Record<Size, Size>> = {
  1: 6,
  2: 10,
  3: 13,
  4: 16,
};

export const Button = memo(
  forwardRef(
    (
      {
        children,
        className,
        color = Color.Primary,
        disabled = false,
        icon,
        loading = false,
        loadingInline = false,
        size = Size.Two,
        style,
        variant = ButtonVariant.Base,
        type = 'button',
        asChild = false,
        ...props
      }: ButtonProps,
      ref
    ) => {
      const [buttonMinWidth, setButtonMinWidth] = useState<number>(0);
      const [buttonMinHeight, setButtonMinHeight] = useState<number>(0);
      const buttonRef = useRef<HTMLButtonElement>(null);
      // Tracks the most recent idle size without triggering re-renders in the observer
      const idleWidthRef = useRef<number>(0);
      const idleHeightRef = useRef<number>(0);
      const classes = clsx(
        styles.button,
        {
          [styles.buttonIcon]:
            (!!icon && !!children && !loadingInline) ||
            (loadingInline && !!children),
          [styles.buttonLoadingInline]: loadingInline,
          [styles.buttonDisabled]: disabled || loading || loadingInline,
          [styles.buttonLight]: isVariantColor(color),
          [styles.buttonBase]: is(variant, ButtonVariant.Base),
          [styles.buttonOutlined]: is(variant, ButtonVariant.Outlined),
          [styles.buttonLink]: is(variant, ButtonVariant.Link),
          [styles.buttonBorderless]: is(variant, ButtonVariant.Borderless),
        },
        className
      );
      const iconElem = loadingInline ? (
        <Spinner size={size} color={BaseColor.Background} />
      ) : !icon || isValidElement(icon) ? (
        icon
      ) : (
        <Icon
          size={buttonIconSizeMap[size]}
          name={icon as Icons}
          // eslint-disable-next-line react/forbid-component-props
          style={{
            '--color': '"inherit"',
          }}
        />
      );

      useLayoutEffect(() => {
        // When idle: release the min-width lock (minWidth: 0 is visually identical for
        // flex-stretched buttons) and capture the current natural size into a ref.
        // When loading: lock state at the last-known idle size from the ref.
        // The ResizeObserver is only created during idle state — buttons that never
        // enter a loading state pay no observer overhead.
        if (!buttonRef.current) return;

        if (!loading && !loadingInline) {
          idleWidthRef.current = buttonRef.current.offsetWidth;
          idleHeightRef.current = buttonRef.current.offsetHeight;
          setButtonMinWidth(0);
          setButtonMinHeight(0);

          const parent = buttonRef.current.parentElement;
          if (!parent) return;

          // Update ref only — no state change, no re-render.
          // buttonMinWidth is 0 here (idle), so offsetWidth is the true flex-stretched size.
          const observer = new ResizeObserver(() => {
            if (!buttonRef.current) return;
            idleWidthRef.current = buttonRef.current.offsetWidth;
            idleHeightRef.current = buttonRef.current.offsetHeight;
          });
          observer.observe(parent);

          return () => observer.disconnect();
        } else {
          setButtonMinWidth(idleWidthRef.current);
          setButtonMinHeight(idleHeightRef.current);
        }
      }, [loading, loadingInline, size]);

      const Comp = asChild ? RadixSlot.Slot : 'button';

      const content = asChild ? (
        children
      ) : loading ? (
        <Spinner size={size} color={BaseColor.Background} />
      ) : iconElem ? (
        children ? (
          <>
            <span>{iconElem}</span>
            <span className={styles.buttonText}>{children}</span>
          </>
        ) : (
          <span className={styles.buttonIconOnly}>{iconElem}</span>
        )
      ) : (
        children
      );

      return (
        <Comp
          type={!asChild ? type : undefined}
          {...props}
          ref={mergeRefs([buttonRef, ref])}
          className={classes}
          disabled={!asChild && (disabled || loading || loadingInline)}
          // eslint-disable-next-line react/forbid-component-props
          style={
            {
              '--size': `var(--base-size-${size})`,
              '--base-color': colorVarMap[color],
              '--button-fixed-width':
                buttonMinWidth > 0 ? `${buttonMinWidth}px` : 'auto',
              minWidth: buttonMinWidth,
              minHeight: buttonMinHeight,
              '--accent-color': isVariantColor(color)
                ? getBaseColor(color)
                  ? colorVarMap[getBaseColor(color)!]
                  : 'black'
                : 'white',
              ...style,
            } as CSSProperties
          }
        >
          {content}
        </Comp>
      );
    }
  )
);
Button.displayName = 'Button';
